import { memo, useState, useCallback, useEffect, type CSSProperties } from 'react'
import { programGenresApi } from '../../lib/api.js'
import { COLLECTION_PRESET_COLORS } from '../../lib/catalog-colors.js'
import type { ProgramGenre } from '../../types/domain.js'
import { ModalShell } from '../common/ModalShell.js'
import './ProgramsView.css'

// ─── Props ────────────────────────────────────────────────────────────────────

interface ProgramGenreModalProps {
  channelId: string
  genre?:    ProgramGenre
  onCreated?: (genre: ProgramGenre) => void
  onUpdated?: (genre: ProgramGenre) => void
  onClose:    () => void
}

// ─── Composant ────────────────────────────────────────────────────────────────

export const ProgramGenreModal = memo(function ProgramGenreModal({ channelId, genre, onCreated, onUpdated, onClose }: ProgramGenreModalProps) {
  const isEdit = !!genre
  const [visible, setVisible] = useState(false)
  const [saving,  setSaving]  = useState(false)
  const [error,   setError]   = useState<string | null>(null)
  const [label,   setLabel]   = useState(genre?.label ?? '')
  const [color,   setColor]   = useState(genre?.color ?? COLLECTION_PRESET_COLORS[0].value)

  useEffect(() => { const t = setTimeout(() => setVisible(true), 10); return () => clearTimeout(t) }, [])
  const handleClose = () => { setVisible(false); setTimeout(onClose, 200) }

  const programCount = genre?._count?.programs ?? 0
  const seriesCount  = genre?._count?.series ?? 0
  const renaming     = isEdit && label.trim() !== genre?.label && (programCount + seriesCount) > 0

  const handleSubmit = useCallback(async (e: React.FormEvent) => {
    e.preventDefault()
    if (!label.trim()) return
    setSaving(true); setError(null)
    try {
      if (isEdit && genre) {
        const updated = await programGenresApi.update(genre.id, { label: label.trim(), color })
        onUpdated?.(updated)
      } else {
        const created = await programGenresApi.create({ channelId, label: label.trim(), color })
        onCreated?.(created)
      }
    } catch (err: any) {
      setError(err.message)
      setSaving(false)
    }
  }, [channelId, isEdit, genre, label, color, onCreated, onUpdated])

  return (
    <ModalShell
      title={isEdit ? 'Modifier le genre' : 'Nouveau genre'}
      size="narrow"
      visible={visible}
      onClose={handleClose}
    >
        <form onSubmit={handleSubmit} className="tp-modal-form">
          {error && (
            <div className="tp-feedback tp-feedback--error" role="alert">
              {error}
            </div>
          )}

          <div className="tp-form-field">
            <label className="tp-field-label" htmlFor="program-genre-label">Nom *</label>
            <input id="program-genre-label" className="tp-input" value={label} onChange={e => setLabel(e.target.value)} required autoFocus placeholder="Ex: Musique, Sport, Société & Économie…" />
            <p className="tp-field-help">
              Le genre est publié tel quel au guide des programmes.
            </p>
          </div>

          {/* Le renommage se propage seul : la fiche référence le genre au lieu
              d'en recopier le libellé. Le dire ici évite qu'un opérateur crée un
              doublon en croyant devoir reprendre chaque fiche. */}
          {renaming && (
            <div className="tp-feedback tp-feedback--info" role="status">
              Le nouveau nom s’appliquera à {programCount} programme{programCount !== 1 ? 's' : ''}
              {seriesCount > 0 && ` et ${seriesCount} série${seriesCount !== 1 ? 's' : ''}`}, et partira
              au guide des programmes à la prochaine publication.
            </div>
          )}

          <fieldset className="tp-form-field">
            <legend className="tp-field-label">Couleur</legend>
            <div className="catalog-color-options">
              {COLLECTION_PRESET_COLORS.map(option => (
                <button
                  key={option.value}
                  type="button"
                  onClick={() => setColor(option.value)}
                  className={`catalog-color-option${color === option.value ? ' is-selected' : ''}`}
                  style={{ '--catalog-accent': option.value } as CSSProperties}
                  aria-label={option.label}
                  aria-pressed={color === option.value}
                />
              ))}
            </div>
          </fieldset>

          <div className="tp-modal-actions">
            <button type="button" onClick={handleClose} className="tp-button">
              Annuler
            </button>
            <button type="submit" disabled={saving || !label.trim()} className="tp-button tp-button--info" data-state={saving ? 'loading' : undefined}>
              {saving ? 'Enregistrement…' : isEdit ? 'Mettre à jour' : 'Créer'}
            </button>
          </div>
        </form>
    </ModalShell>
  )
})
